/** Keys used for breakdown rows / i18n; `climate_impact` reserved — not rendered yet. */
export type FactorKey =
  | "regular_projection"
  | "climate_impact"
  | "market_price_trend"
  | "input_cost_trend"
  | "yield_quality"
  | "cost_savings"
  | "resilience"
  | "yield_amount"
  | "quality_improvement"
  | "pest_pressure"
  | "nitrogen"
  | "leaching"
  | "runoff"
  | "erosion"
  | "water_holding";

export type FactorCategoryKey = "yield_quality" | "cost_savings" | "resilience";

export const LEAF_SUM_KEYS = [
  "regular_projection",
  "market_price_trend",
  "input_cost_trend",
  "yield_amount",
  "quality_improvement",
  "pest_pressure",
  "nitrogen",
  "leaching",
  "runoff",
  "erosion",
  "water_holding",
] as const satisfies readonly FactorKey[];

export type ImpactLeafFactorKey = (typeof LEAF_SUM_KEYS)[number];

export interface FactorUiDef {
  i18nLabelKey: string;
  i18nDescriptionKey: string;
  // Used instead when the CM breakdown flag is on and the description names a factor that flag removes.
  i18nCmBreakdownDescriptionKey?: string;
  color: string;
}

export const CATEGORY_ORDER: FactorCategoryKey[] = ["yield_quality", "cost_savings", "resilience"];

export const SUB_FACTOR_ORDER: Record<FactorCategoryKey, ImpactLeafFactorKey[]> = {
  yield_quality: ["yield_amount", "quality_improvement"],
  cost_savings: ["pest_pressure", "nitrogen"],
  resilience: ["leaching", "runoff", "erosion", "water_holding"],
};

/** Map factor key → Crowdin-ish key suffix under outcomeComparison.impactFactors */
export const FACTOR_UI: Record<Exclude<FactorKey, "climate_impact">, Omit<FactorUiDef, never> & { category?: FactorCategoryKey }> = {
  regular_projection: {
    i18nLabelKey: "regularProjectionLabel",
    i18nDescriptionKey: "regularProjectionDescription",
    color: "#94A3B8",
  },
  market_price_trend: {
    i18nLabelKey: "marketPriceTrendLabel",
    i18nDescriptionKey: "marketPriceTrendDescription",
    color: "#065C45",
  },
  input_cost_trend: {
    i18nLabelKey: "inputCostTrendLabel",
    i18nDescriptionKey: "inputCostTrendDescription",
    color: "#065C45",
  },
  yield_quality: {
    i18nLabelKey: "yieldQualityCategoryLabel",
    i18nDescriptionKey: "yieldQualityCategoryDescription",
    color: "#065C45",
    category: "yield_quality",
  },
  cost_savings: {
    i18nLabelKey: "costSavingsCategoryLabel",
    i18nDescriptionKey: "costSavingsCategoryDescription",
    color: "#3F6ED6",
    category: "cost_savings",
  },
  resilience: {
    i18nLabelKey: "resilienceCategoryLabel",
    i18nDescriptionKey: "resilienceCategoryDescription",
    i18nCmBreakdownDescriptionKey: "resilienceCategoryDescriptionCmBreakdown",
    color: "#43B8CF",
    category: "resilience",
  },
  yield_amount: {
    i18nLabelKey: "yieldAmountLabel",
    i18nDescriptionKey: "yieldAmountDescription",
    color: "#065C45",
    category: "yield_quality",
  },
  quality_improvement: {
    i18nLabelKey: "qualityImprovementLabel",
    i18nDescriptionKey: "qualityImprovementDescription",
    color: "#1F8A66",
    category: "yield_quality",
  },
  pest_pressure: {
    i18nLabelKey: "pestPressureLabel",
    i18nDescriptionKey: "pestPressureDescription",
    color: "#3F6ED6",
    category: "cost_savings",
  },
  nitrogen: {
    i18nLabelKey: "nitrogenLabel",
    i18nDescriptionKey: "nitrogenDescription",
    color: "#5B8DE8",
    category: "cost_savings",
  },
  leaching: {
    i18nLabelKey: "leachingLabel",
    i18nDescriptionKey: "leachingDescription",
    color: "#43B8CF",
    category: "resilience",
  },
  runoff: {
    i18nLabelKey: "runoffLabel",
    i18nDescriptionKey: "runoffDescription",
    color: "#7CCBDB",
    category: "resilience",
  },
  erosion: {
    i18nLabelKey: "erosionLabel",
    i18nDescriptionKey: "erosionDescription",
    color: "#E77307",
    category: "resilience",
  },
  water_holding: {
    i18nLabelKey: "waterHoldingLabel",
    i18nDescriptionKey: "waterHoldingDescription",
    color: "#E8B14C",
    category: "resilience",
  },
};
